import React, { useEffect, useMemo, useState } from 'react'
import "../../css/hire-view.css"
import MainBreadcrumb from '../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb'
import { BriefcaseIcon, EditIcon, HomeIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter'
import { Button, Empty } from '@core/uilibrary'
import { useNavigate, useParams } from 'react-router-dom'
import { getJobDetailApi, JobDetailItem } from './hire.type'

type RichBlock = { desc: string; html: string };

const safeParseRich = (
  raw?: string | null,
  keys?: { desc: string; html: string }
): RichBlock => {
  const k = keys ?? { desc: "responsibleDescription", html: "responsibleHtml" };
  if (!raw) return { desc: "-", html: "" };

  try {
    const obj = JSON.parse(raw);
    return {
      desc: typeof obj?.[k.desc] === "string" ? obj[k.desc] : "-",
      html: typeof obj?.[k.html] === "string" ? obj[k.html] : "",
    };
  } catch {
    return { desc: String(raw), html: "" };
  }
};

const RichSection = ({ block }: { block: RichBlock }) => {
  return (
    <>
      {block.desc && block.desc !== "-" ? <p>{block.desc}</p> : null}
      {block.html ? <div dangerouslySetInnerHTML={{ __html: block.html }} /> : null}
    </>
  );
};

const HireView = () => {
  const navigate = useNavigate();
  const { careerId } = useParams<{ careerId: string }>();

  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState<JobDetailItem | null>(null);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!careerId) return;

      try {
        setLoading(true);
        const res = await getJobDetailApi(careerId);
        if (!alive) return;
        setJob(res?.data?.data ?? null);
      } catch (e) {
        console.error("GET job detail failed:", e);
        if (alive) setJob(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [careerId]);

  const responsibilities = useMemo(
    () =>
      safeParseRich(job?.responsibilities, {
        desc: "responsibleDescription",
        html: "responsibleHtml",
      }),
    [job?.responsibilities]
  );

  const requirements = useMemo(
    () =>
      safeParseRich(job?.requirements, {
        desc: "requirementsDescription",
        html: "requirementsHtml",
      }),
    [job?.requirements]
  );

  const offer = useMemo(
    () =>
      safeParseRich(job?.offer, {
        desc: "offerDescription",
        html: "offerHtml",
      }),
    [job?.offer]
  );

  if (!careerId) {
    return (
      <main className="scl--crr-hv-const-styl-main">
        <Empty title="Missing job id" description="No careerId provided in the URL." />
      </main>
    );
  }

  if (!loading && !job) {
    return (
      <main className="scl--crr-hv-const-styl-main">
        <Empty title="Not found" description="This job does not exist or cannot be loaded." />
      </main>
    );
  }

  const jobCode = job?.jobCode ?? "-";
  const jobTitle = job?.jobTitle ?? "-";
  const jobLevel = job?.jobLevelName ?? "-";
  const jobType = job?.jobType ?? "-";
  const department = job?.departmentName ?? "-";
  const status = job?.status ?? "-";
  const jobDescription = job?.description ?? "-";

  return (
    <main className='scl--crr-hv-const-styl-main'>
      <section className='scl--crr-hv-const-bread-sec-on'>
        <button onClick={() => navigate("/career/hire")}>
          <HomeIcon />
          <span className='span-bread'>
            Hiring
          </span>
        </button>
        <span>/</span>
        <h6>
          <BriefcaseIcon />
          <span className='span-bread'>
            View
          </span>
        </h6>
      </section>

      <section className='scl--crr-hv-const-title-sec-tw'>
        <MainBreadcrumb
          title="View Application"
          paragraph="Controls how content is created, reviewed, published, and managed to maintain."
        />
        <button
          className='btn-section-two'
          onClick={() => navigate(`/career/hire/${careerId}/edit`)}
        >
          <EditIcon />
        </button>
      </section>

      <section className='scl--crr-hv-const-jb-title-sec-thd'>
        <div className='scl--crr-hv-item-const-main-box'>
          <div className='title-job'>
            <h6>Job ID: </h6>
            <span>{jobCode}</span>
          </div>

          <div className='title-job'>
            <h6>Job Title: </h6>
            <span>{jobTitle}</span>
          </div>
        </div>

        <div className='scl--crr-hv-item-const-main-box'>
          <div className='title-job'>
            <h6>Job level: </h6>
            <span>{jobLevel}</span>
          </div>

          <div className='title-job'>
            <h6>Job type: </h6>
            <span>{jobType}</span>
          </div>
        </div>

        <div className='scl--crr-hv-item-const-main-box'>
          <div className='title-job'>
            <h6>Department: </h6>
            <span>{department}</span>
          </div>

          <div className='title-job'>
            <h6>Status:</h6>
            <span>{status}</span>
          </div>
        </div>
      </section>


      <section className='scl--crr-hv-const-jb-desc-sec-fth'>
        <div className="scl--crr-hv-jb-des-det-sty">
          <h6>Job Description</h6>
          <p>{jobDescription}</p>
        </div>

        <div className='scl--crr-hv-jb-des-det-sty wierd-ul'>
          <h6>Key Responsibilities</h6>
          <RichSection block={responsibilities} />
        </div>

        <div className='scl--crr-hv-jb-des-det-sty'>
          <h6>Skill requirements</h6>
          <RichSection block={requirements} />
        </div>

        <div className='scl--crr-hv-jb-des-det-sty'>
          <h6>What we offer</h6>
          <RichSection block={offer} />
        </div>
      </section>

      <section className='scl--crr-hv-const-jb-btn-sec-fith'>
        <Button
          variant="ghost"
          value="back"
          type="button"
          onClick={() => navigate("/career/hire")}
        />
      </section>
    </main>
  )
}

export default HireView